import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { formatDate, type CourseOverview } from '../api.ts';
import { isTeacher, useAuth } from '../auth.tsx';
import { Container } from '../components/Layout.tsx';
import { AuroraRing, Badge as Medal, Journey, MiniRing, type JourneyStop } from '../components/Progress.tsx';
import { KnowledgeCompare } from '../components/KnowledgeCompare.tsx';
import { NorrbottenMap } from '../components/NorrbottenMap.tsx';
import { Segments } from '../components/Segments.tsx';
import { Alert, Badge, CheckIcon, cx, KindIcon, kindLabel, LinkButton, Loading } from '../components/ui.tsx';
import { useApi, useTitle } from '../hooks.ts';

type Module = CourseOverview['modules'][number];

function greeting(name: string | undefined, done: number, left: number, allDone: boolean): string {
  if (allDone) return `Grattis ${name}! Du har gjort hela kursen. Ditt intyg väntar på dig.`;
  if (done === 0) return `Välkommen ${name}! Din resa genom AI:ns värld börjar här – i din egen takt.`;
  if (left <= 3)
    return `Snart framme, ${name}! Bara ${left} ${left === 1 ? 'del' : 'delar'} kvar till intyget.`;
  return `Bra jobbat, ${name}! ${left} delar kvar till ditt intyg.`;
}

function minutesLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export function CoursePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: focusModule } = useParams();
  const { data, error, loading } = useApi<CourseOverview>('/api/course');
  const [openModules, setOpenModules] = useState<Set<number>>(new Set());
  useTitle(data?.course.title ?? 'Kursen');

  const isDone = (m: Module) => m.required > 0 && m.completed === m.required;
  const currentModule = data?.modules.find((m) => !m.locked && !isDone(m)) ?? null;

  useEffect(() => {
    if (!data) return;
    const initial = new Set<number>();
    if (focusModule) initial.add(Number(focusModule));
    else if (currentModule) initial.add(currentModule.id);
    setOpenModules(initial);
    if (focusModule) {
      window.setTimeout(
        () =>
          document
            .getElementById(`modul-${focusModule}`)
            ?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
        50,
      );
    }
  }, [data, focusModule]);

  if (loading && !data) return <Loading />;
  if (error?.code === 'no_cohort') {
    return (
      <Container narrow>
        <Alert kind="info">
          Det finns ingen kursomgång öppen just nu. Du får ett mejl när nästa omgång startar.
        </Alert>
      </Container>
    );
  }
  if (error || !data) {
    return (
      <Container narrow>
        <Alert>{error?.message ?? 'Kursen kunde inte hämtas.'}</Alert>
      </Container>
    );
  }

  const { progress } = data;
  const left = progress.required - progress.completed;
  const allItems = data.modules.flatMap((m) => m.items);
  const deepdives = allItems.filter((i) => i.kind === 'deepdive');
  const modulesDone = data.modules.filter(isDone).length;
  const minutesLeft = data.modules
    .flatMap((m) => m.items)
    .filter((i) => i.required && !i.completed)
    .reduce((sum, i) => sum + (i.estimatedMinutes ?? 0), 0);
  const resume = allItems.find((i) => i.id === data.resumeItemId) ?? null;

  const stops: JourneyStop[] = [
    ...data.modules.map((m) => ({
      id: m.id,
      label: m.title,
      done: isDone(m),
      current: currentModule?.id === m.id,
      locked: m.locked,
      fraction: m.required ? m.completed / m.required : 0,
    })),
    { id: 'intyg', label: 'Intyg', done: !!data.certificate, current: progress.allDone && !data.certificate },
  ];

  const toggle = (id: number) =>
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <section className="aurora-sky overflow-hidden text-white">
        <div className="stars" />
        <div className="relative mx-auto flex max-w-6xl flex-col gap-10 px-4 pb-24 pt-10 sm:pt-14 md:flex-row md:items-center md:justify-between">
          <div className="fade-up max-w-xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-aurora-400">
              {data.cohort.name}
            </p>
            <h1 className="mt-2 text-3xl font-extrabold leading-tight sm:text-5xl">{data.course.title}</h1>
            <p className="mt-4 text-lg text-white/85">
              {greeting(user?.firstName, progress.completed, left, progress.allDone)}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              {data.certificate ? (
                <LinkButton
                  to="/intyg"
                  className="bg-gradient-to-r from-amber-400 to-yellow-300 !text-night-950 shadow-lg hover:brightness-105"
                >
                  ★ Hämta ditt intyg
                </LinkButton>
              ) : (
                resume && (
                  <Link
                    to={`/kurs/del/${resume.id}`}
                    className="group inline-flex items-center gap-3 rounded-xl bg-white px-5 py-3 text-night-900 shadow-xl transition hover:-translate-y-0.5"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-aurora-500 to-violet-aurora text-white">
                      ▶
                    </span>
                    <span className="text-left">
                      <span className="block text-xs font-semibold uppercase tracking-wide text-muted">
                        {progress.completed === 0 ? 'Börja här' : 'Fortsätt där du var'}
                      </span>
                      <span className="block max-w-[16rem] truncate font-bold">{resume.title}</span>
                    </span>
                  </Link>
                )
              )}
            </div>
          </div>
          <div className="fade-up flex items-center gap-6 self-center md:self-auto">
            <AuroraRing percent={progress.percent} />
            <dl className="grid gap-3 text-sm">
              <Stat label="Klara delar" value={`${progress.completed} / ${progress.required}`} />
              <Stat label="Modulmärken" value={`${modulesDone} / ${data.modules.length}`} />
              {minutesLeft > 0 && !progress.allDone && (
                <Stat label="Tid kvar, cirka" value={minutesLabel(minutesLeft)} />
              )}
              {deepdives.length > 0 && (
                <Stat
                  label="Fördjupningar"
                  value={`${deepdives.filter((d) => d.completed).length} / ${deepdives.length}`}
                  to="/lasning"
                />
              )}
            </dl>
          </div>
        </div>
      </section>

      <Container>
        <div className="-mt-20 space-y-6">
          <div className="fade-up rounded-2xl bg-white p-5 shadow-xl ring-1 ring-night-800/5 sm:p-7">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-bold text-night-900">Din resa</h2>
              <p className="text-sm text-muted">
                {progress.allDone
                  ? 'Alla stationer avklarade!'
                  : currentModule
                    ? `Du är i: ${currentModule.title}`
                    : ''}
              </p>
            </div>
            <Journey
              stops={stops}
              onSelect={(s) => {
                if (s.id === 'intyg') navigate('/intyg');
                else {
                  setOpenModules((prev) => new Set(prev).add(Number(s.id)));
                  document
                    .getElementById(`modul-${s.id}`)
                    ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                }
              }}
            />
          </div>

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-night-800/5 sm:p-7">
            <h2 className="mb-4 text-lg font-bold text-night-900">Dina märken</h2>
            <div className="flex flex-wrap gap-3">
              {data.modules.map((m, i) => (
                <Medal key={m.id} earned={isDone(m)} number={i + 1} label={m.title} />
              ))}
              <Medal kind="certificate" earned={!!data.certificate} label="Intyg" />
            </div>
            {data.achievements.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-3 border-t border-night-100 pt-5">
                {data.achievements.map((a) => (
                  <div key={a.key} title={a.description}>
                    <Medal
                      kind="achievement"
                      earned={a.earned}
                      label={a.label}
                      icon={ACHIEVEMENT_ICONS[a.key]}
                      hint={a.earned ? undefined : a.progress}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {data.knowledgeCheck && <KnowledgeCard check={data.knowledgeCheck} />}

          <UpcomingMeetings />

          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-night-800/5 sm:p-7">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-bold text-night-900">Norrbotten tillsammans</h2>
              <Link to="/norrbotten" className="text-sm font-semibold text-night-700 underline">
                Hela kartan
              </Link>
            </div>
            <NorrbottenMap highlight={user?.municipality} compact />
          </div>

          {data.course.description.length > 0 && (
            <details className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-night-800/5 sm:p-7">
              <summary className="cursor-pointer text-lg font-bold text-night-800">Om kursen</summary>
              <div className="mt-4">
                <Segments segments={data.course.description} />
              </div>
            </details>
          )}

          <ol className="space-y-4">
            {data.modules.map((m, index) => (
              <ModuleCard
                editable={isTeacher(user)}
                key={m.id}
                module={m}
                index={index}
                open={openModules.has(m.id)}
                current={currentModule?.id === m.id}
                nextItemId={data.resumeItemId}
                onToggle={() => toggle(m.id)}
              />
            ))}
          </ol>
        </div>
      </Container>
    </>
  );
}

interface MeetingsResponse {
  feedUrl: string;
  upcoming: {
    id: number;
    title: string;
    startsAt: string;
    durationMinutes: number;
    location: string | null;
  }[];
}

function UpcomingMeetings() {
  const { data } = useApi<MeetingsResponse>('/api/course/meetings');
  const [copied, setCopied] = useState(false);
  if (!data || data.upcoming.length === 0) return null;
  const webcal = data.feedUrl.replace(/^https?:/, 'webcal:');
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-night-800/5 sm:p-7">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-bold text-night-900">Kommande träffar</h2>
        <div className="flex flex-wrap gap-3 text-sm">
          <a href={webcal} className="font-semibold text-night-700 underline">
            Prenumerera i din kalender
          </a>
          <button
            type="button"
            className="font-semibold text-night-700 underline"
            onClick={async () => {
              await navigator.clipboard?.writeText(data.feedUrl);
              setCopied(true);
            }}
          >
            {copied ? 'Länken kopierad ✓' : 'Kopiera kalenderlänk'}
          </button>
        </div>
      </div>
      <ul className="divide-y divide-night-100">
        {data.upcoming.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-3 py-3">
            <span className="flex h-11 w-11 flex-col items-center justify-center rounded-xl bg-night-50 text-night-800 ring-1 ring-night-800/10">
              <span className="text-[10px] font-semibold uppercase leading-none">
                {new Intl.DateTimeFormat('sv-SE', { month: 'short', timeZone: 'Europe/Stockholm' }).format(
                  new Date(m.startsAt),
                )}
              </span>
              <span className="text-lg font-extrabold leading-none">
                {new Intl.DateTimeFormat('sv-SE', { day: 'numeric', timeZone: 'Europe/Stockholm' }).format(
                  new Date(m.startsAt),
                )}
              </span>
            </span>
            <span className="min-w-0 flex-1">
              <Link to={`/kurs/del/${m.id}`} className="block font-semibold text-night-900 hover:underline">
                {m.title}
              </Link>
              <span className="text-sm text-muted">
                {formatDate(m.startsAt, true)}
                {m.location ? ` · ${m.location}` : ''}
              </span>
            </span>
            <a
              href={`/api/course/items/${m.id}/calendar.ics`}
              download
              className="text-sm font-semibold text-night-700 underline"
            >
              Lägg till i kalendern
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function KnowledgeCard({ check }: { check: NonNullable<CourseOverview['knowledgeCheck']> }) {
  const pre = check.pre?.percent ?? null;
  const post = check.post?.percent ?? null;
  const next = pre === null ? check.pre : post === null ? check.post : null;
  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-night-800/5 sm:p-7">
      <h2 className="mb-1 text-lg font-bold text-night-900">Din kunskapsresa</h2>
      <p className="mb-4 text-sm text-muted">
        {pre !== null && post !== null
          ? post > pre
            ? `Du har gått från ${pre} % till ${post} % – ${post - pre} procentenheter bättre än när du började!`
            : `Du fick ${pre} % i början och ${post} % i slutet.`
          : pre !== null
            ? 'Så här gick det i början. I slutet av kursen gör du en likadan kontroll och ser vad du lärt dig.'
            : 'En kort kunskapskontroll i början och en i slutet visar hur mycket du lärt dig.'}
      </p>
      <KnowledgeCompare pre={pre} post={post} />
      {next && (
        <Link
          to={`/kurs/del/${next.itemId}`}
          className="mt-4 inline-block text-sm font-semibold text-night-700 underline"
        >
          {next.title} →
        </Link>
      )}
    </div>
  );
}

const ACHIEVEMENT_ICONS: Record<string, string> = {
  explorer: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm3.5 5.5-2 5-5 2 2-5z',
  reflective: 'M5 4h11l3 3v13H5zM8 9h8M8 13h8M8 17h5',
  classroom: 'M3 20h18M5 20V9l7-5 7 5v11M9 20v-6h6v6',
  conversation: 'M4 5h11v8H8l-4 3zM9 16h7l4 3v-9h-3',
};

function Stat({ label, value, to }: { label: string; value: string; to?: string }) {
  return (
    <div className="rounded-xl bg-white/10 px-4 py-2 ring-1 ring-white/15 backdrop-blur">
      <dt className="text-xs text-white/70">{label}</dt>
      <dd className="text-lg font-bold tabular-nums">
        {to ? (
          <Link to={to} className="hover:underline">
            {value} <span className="text-sm font-semibold text-aurora-400">läslistan →</span>
          </Link>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function ModuleCard({
  module: m,
  index,
  open,
  current,
  nextItemId,
  onToggle,
  editable,
}: {
  /** Läraren ser en genväg till modulen i lärarvyn. */
  editable: boolean;
  module: Module;
  index: number;
  open: boolean;
  current: boolean;
  nextItemId: number | null;
  onToggle: () => void;
}) {
  const done = m.required > 0 && m.completed === m.required;
  const percent = m.required ? Math.round((m.completed / m.required) * 100) : 0;
  return (
    <li
      id={`modul-${m.id}`}
      className={cx(
        'scroll-mt-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 transition',
        current ? 'ring-2 ring-aurora-400/70' : 'ring-night-800/5',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={m.locked}
        aria-expanded={open}
        className="flex w-full items-center gap-4 p-5 text-left hover:bg-night-50/60 sm:p-6"
      >
        {done ? (
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-aurora-500 to-violet-aurora text-lg text-white shadow">
            ★
          </span>
        ) : (
          <MiniRing percent={percent}>{m.locked ? '🔒' : index + 1}</MiniRing>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-lg font-bold text-night-900">
              <span className="sr-only">Modul {index + 1}: </span>
              {m.title}
            </span>
            {current && <Badge tone="green">Pågår</Badge>}
            {done && <Badge tone="green">Märke taget</Badge>}
          </span>
          <span className="mt-0.5 block text-sm text-muted">
            {m.locked
              ? m.lockReason === 'previous'
                ? 'Öppnas när du är klar med föregående modul'
                : `Öppnar ${formatDate(m.unlockAt, true)}`
              : `${m.completed} av ${m.required} klara${m.minutes ? ` · cirka ${minutesLabel(m.minutes)}` : ''}`}
          </span>
        </span>
        {!m.locked && (
          <svg
            viewBox="0 0 24 24"
            className={cx('h-5 w-5 shrink-0 text-muted transition', open && 'rotate-180')}
            aria-hidden="true"
          >
            <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </button>
      {editable && (
        <p className="-mt-2 px-5 pb-4 sm:px-6">
          <Link
            to={`/larare/innehall#modul-${m.id}`}
            className="text-sm font-semibold text-night-700 underline hover:text-night-900"
          >
            ✏️ Redigera modulen<span className="sr-only"> {m.title}</span>
          </Link>
        </p>
      )}
      {open && !m.locked && (
        <ul className="divide-y divide-night-100 border-t border-night-100">
          {m.items.map((item) => {
            const next = item.id === nextItemId && !item.completed;
            return (
              <li key={item.id}>
                <Link
                  to={`/kurs/del/${item.id}`}
                  className={cx(
                    'flex items-center gap-3 px-5 py-3 transition sm:px-6',
                    next ? 'bg-aurora-100/60 hover:bg-aurora-100' : 'hover:bg-night-50',
                  )}
                >
                  <span
                    className={cx(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                      item.completed ? 'bg-aurora-100 text-aurora-600' : 'bg-night-50 text-night-700',
                    )}
                  >
                    <KindIcon kind={item.kind} className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cx('font-medium', item.completed && 'text-muted')}>{item.title}</span>
                    <span className="ml-2 text-xs text-muted">
                      {kindLabel(item.kind)}
                      {item.estimatedMinutes ? ` · ${item.estimatedMinutes} min` : ''}
                    </span>
                  </span>
                  {next && <Badge tone="green">Nästa</Badge>}
                  {!item.required && <Badge tone="blue">Frivillig</Badge>}
                  {item.completed ? (
                    <CheckIcon className="text-aurora-500" />
                  ) : (
                    <span className="h-5 w-5 rounded-full border-2 border-night-800/20" aria-hidden="true" />
                  )}
                  <span className="sr-only">{item.completed ? 'klar' : 'inte klar'}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
